import SpecsRevisionService from "../../../service/SpecsRevisionService";
import { callSam3 } from "../callSam3";
import { getCached, setCached } from "../falCache";
import {
  uploadSpecsImageUrl,
  buildSpecsLayerImageFieldPath,
} from "../specsImageUpload";
import type { SpecsEditorSession } from "../types";
import {
  buildImportProductSnapshot,
  getImportPartLabel,
} from "./buildImportProductSnapshot";
import { detectImportParts } from "./detectImportParts";
import { processImportUploadImageUrl } from "./importImageProcessing";
import type {
  DetectedImportParts,
  ImportGeneratedLayerAsset,
  ImportImageInput,
  ImportImageSide,
  ImportPartKey,
  ImportProductSnapshotInput,
} from "./types";

type ProgressStep = {
  label: string;
  status: "pending" | "running" | "done" | "error";
  detail?: string;
};

export type ImportRunInput = {
  productName: string;
  displayName: string;
  versionName: string;
  idPrextra: string;
  specsServiceUrl?: string;
  frontInput: ImportImageInput;
  backInput: ImportImageInput | null;
  falSettings: SpecsEditorSession["falSettings"];
  createdBy: string;
  onStepsChange: (steps: ProgressStep[]) => void;
};

export type PreparedImportAssets = {
  generatedLayers: ImportGeneratedLayerAsset[];
  globalSides: ImportProductSnapshotInput["globalSides"];
};

export type ImportPrepareInput = {
  productName: string;
  specsServiceUrl?: string;
  frontInput: ImportImageInput;
  backInput: ImportImageInput | null;
  falSettings: SpecsEditorSession["falSettings"];
  onStepsChange: (steps: ProgressStep[]) => void;
};

export type ImportCreateRevisionInput = {
  productName: string;
  displayName: string;
  versionName: string;
  idPrextra: string;
  specsServiceUrl?: string;
  preparedAssets: PreparedImportAssets;
  createdBy: string;
  steps: ProgressStep[];
  onStepsChange: (steps: ProgressStep[]) => void;
};

export type ImportRunResult = {
  productName: string;
  versionName: string;
  revisionId: string;
};

const SAM_PROMPTS: Record<ImportPartKey, string> = {
  body: "garment body fabric",
  laces: "drawstring laces cords",
  zips: "zipper zip pull",
  hand_pockets: "hoodie hand pocket",
};

const IMPORT_LAYER_ASSET_CACHE_MODEL = "specs-import-layer-asset:v2";

function createInitialSteps(hasBack: boolean): ProgressStep[] {
  return [
    { label: "Detect front layers", status: "pending" },
    ...(hasBack
      ? [{ label: "Detect back layers", status: "pending" as const }]
      : []),
    { label: "Segment and upload layer images", status: "pending" },
    { label: "Upload global images", status: "pending" },
    { label: "Create product revision", status: "pending" },
  ];
}

function updateStep(
  steps: ProgressStep[],
  index: number,
  patch: Partial<ProgressStep>
) {
  return steps.map((step, stepIndex) =>
    stepIndex === index ? { ...step, ...patch } : step
  );
}

function getCreateRevisionStepIndex(steps: ProgressStep[]) {
  const index = steps.findIndex(
    (step) => step.label === "Create product revision"
  );
  if (index === -1) {
    throw new Error("Missing create product revision import step");
  }
  return index;
}

function mergeDetectedParts(
  frontParts: DetectedImportParts,
  backParts: DetectedImportParts | null
) {
  return {
    body: true,
    laces: frontParts.laces || backParts?.laces === true,
    zips: frontParts.zips || backParts?.zips === true,
    hand_pockets: frontParts.hand_pockets || backParts?.hand_pockets === true,
  };
}

async function uploadGeneratedImage(args: {
  imageUrl: string;
  productName: string;
  specsServiceUrl?: string;
  part: ImportPartKey;
  side: ImportImageSide;
  role: "highlight" | "mask";
}) {
  const layerLabel = getImportPartLabel(args.part);
  const fileNameBase = [args.productName, args.part, args.side, args.role].join(
    "-"
  );
  const fieldPath = buildSpecsLayerImageFieldPath({
    partName: args.part,
    elementName: args.part,
    styleName: "solid",
    layerName: layerLabel,
    role: args.role,
    side: args.side,
  });
  const processedImageUrl = await processImportUploadImageUrl(
    args.imageUrl,
    args.role
  );

  return uploadSpecsImageUrl(processedImageUrl, fileNameBase, {
    specType: "product",
    specName: args.productName,
    fieldPath,
    specsServiceUrl: args.specsServiceUrl,
  });
}

function createLayerAssetCachePrompt(args: {
  productName: string;
  specsServiceUrl?: string;
  part: ImportPartKey;
  side: ImportImageSide;
}) {
  return JSON.stringify({
    productName: args.productName,
    specsServiceUrl: args.specsServiceUrl ?? "",
    part: args.part,
    side: args.side,
    prompt: SAM_PROMPTS[args.part],
  });
}

function parseCachedLayerAsset(
  cached: string | null,
  expected: { part: ImportPartKey; side: ImportImageSide }
): ImportGeneratedLayerAsset | null {
  if (!cached) {
    return null;
  }

  try {
    const parsed = JSON.parse(cached) as Record<string, unknown>;
    if (
      parsed.part !== expected.part ||
      parsed.side !== expected.side ||
      typeof parsed.label !== "string" ||
      typeof parsed.imageUrl !== "string" ||
      typeof parsed.highlightUrl !== "string"
    ) {
      return null;
    }

    return {
      part: expected.part,
      label: parsed.label,
      side: expected.side,
      imageUrl: parsed.imageUrl,
      highlightUrl: parsed.highlightUrl,
    };
  } catch (error) {
    console.error("Failed to parse cached import layer asset", error);
    return null;
  }
}

async function createLayerAsset(args: {
  productName: string;
  specsServiceUrl?: string;
  part: ImportPartKey;
  side: ImportImageSide;
  sourceImageUrl: string;
  falApiKey: string;
}) {
  const cachePrompt = createLayerAssetCachePrompt(args);
  const cachedAsset = parseCachedLayerAsset(
    await getCached(
      args.sourceImageUrl,
      cachePrompt,
      IMPORT_LAYER_ASSET_CACHE_MODEL
    ),
    args
  );
  if (cachedAsset) {
    return cachedAsset;
  }

  const label = getImportPartLabel(args.part);
  const maskResult = await callSam3(
    args.sourceImageUrl,
    SAM_PROMPTS[args.part],
    args.falApiKey,
    { applyMask: true }
  );
  const maskUrl = maskResult.masks[0]?.url ?? args.sourceImageUrl;
  const uploadedMaskUrl = await uploadGeneratedImage({
    imageUrl: maskUrl,
    productName: args.productName,
    specsServiceUrl: args.specsServiceUrl,
    part: args.part,
    side: args.side,
    role: "mask",
  });
  const uploadedHighlightUrl = await uploadGeneratedImage({
    imageUrl: args.sourceImageUrl,
    productName: args.productName,
    specsServiceUrl: args.specsServiceUrl,
    part: args.part,
    side: args.side,
    role: "highlight",
  });

  const asset = {
    part: args.part,
    label,
    side: args.side,
    imageUrl: uploadedMaskUrl,
    highlightUrl: uploadedHighlightUrl,
  };

  void setCached(
    args.sourceImageUrl,
    cachePrompt,
    IMPORT_LAYER_ASSET_CACHE_MODEL,
    JSON.stringify(asset)
  );

  return asset;
}

export async function runImportProductCreation({
  productName,
  displayName,
  versionName,
  idPrextra,
  specsServiceUrl,
  frontInput,
  backInput,
  falSettings,
  createdBy,
  onStepsChange,
}: ImportRunInput): Promise<ImportRunResult> {
  const preparedAssets = await prepareImportProductAssets({
    productName,
    specsServiceUrl,
    frontInput,
    backInput,
    falSettings,
    onStepsChange,
  });

  return createImportProductRevision({
    productName,
    displayName,
    versionName,
    idPrextra,
    specsServiceUrl,
    preparedAssets,
    createdBy,
    steps: createInitialSteps(Boolean(backInput)).map((step) =>
      step.label === "Create product revision"
        ? step
        : { ...step, status: "done" as const }
    ),
    onStepsChange,
  });
}

export async function prepareImportProductAssets({
  productName,
  specsServiceUrl,
  frontInput,
  backInput,
  falSettings,
  onStepsChange,
}: ImportPrepareInput): Promise<PreparedImportAssets> {
  if (!falSettings.apiKey) {
    throw new Error("fal.ai API key is required");
  }

  let steps = createInitialSteps(Boolean(backInput));
  const setStep = (index: number, patch: Partial<ProgressStep>) => {
    steps = updateStep(steps, index, patch);
    onStepsChange(steps);
  };

  onStepsChange(steps);

  let stepIndex = 0;
  setStep(stepIndex, { status: "running" });
  const frontParts = await detectImportParts(
    frontInput.compositeDataUrl,
    falSettings.apiKey
  );
  setStep(stepIndex, {
    status: "done",
    detail: Object.entries(frontParts)
      .filter(([, enabled]) => enabled)
      .map(([part]) => getImportPartLabel(part as ImportPartKey))
      .join(", "),
  });
  stepIndex += 1;

  let backParts: DetectedImportParts | null = null;
  if (backInput) {
    setStep(stepIndex, { status: "running" });
    const detectedBackParts = await detectImportParts(
      backInput.compositeDataUrl,
      falSettings.apiKey
    );
    backParts = detectedBackParts;
    setStep(stepIndex, {
      status: "done",
      detail: Object.entries(detectedBackParts)
        .filter(([, enabled]) => enabled)
        .map(([part]) => getImportPartLabel(part as ImportPartKey))
        .join(", "),
    });
    stepIndex += 1;
  }

  const detectedParts = mergeDetectedParts(frontParts, backParts);
  const enabledParts = (
    Object.entries(detectedParts) as Array<[ImportPartKey, boolean]>
  )
    .filter(([, enabled]) => enabled)
    .map(([part]) => part);

  setStep(stepIndex, { status: "running" });
  const generatedLayers: ImportGeneratedLayerAsset[] = [];
  for (const part of enabledParts) {
    generatedLayers.push(
      await createLayerAsset({
        productName,
        specsServiceUrl,
        part,
        side: "front",
        sourceImageUrl: frontInput.compositeDataUrl,
        falApiKey: falSettings.apiKey,
      })
    );

    if (backInput) {
      generatedLayers.push(
        await createLayerAsset({
          productName,
          specsServiceUrl,
          part,
          side: "back",
          sourceImageUrl: backInput.compositeDataUrl,
          falApiKey: falSettings.apiKey,
        })
      );
    }
  }
  setStep(stepIndex, { status: "done" });
  stepIndex += 1;

  setStep(stepIndex, { status: "running" });
  const uploadedFrontGlobalImageUrl = await uploadGeneratedImage({
    imageUrl: frontInput.compositeDataUrl,
    productName,
    specsServiceUrl,
    part: "body",
    side: "front",
    role: "mask",
  });
  const uploadedFrontGlobalHighlightUrl = await uploadGeneratedImage({
    imageUrl: frontInput.compositeDataUrl,
    productName,
    specsServiceUrl,
    part: "body",
    side: "front",
    role: "highlight",
  });
  const uploadedBackGlobalImageUrl = backInput
    ? await uploadGeneratedImage({
        imageUrl: backInput.compositeDataUrl,
        productName,
        specsServiceUrl,
        part: "body",
        side: "back",
        role: "mask",
      })
    : null;
  const uploadedBackGlobalHighlightUrl = backInput
    ? await uploadGeneratedImage({
        imageUrl: backInput.compositeDataUrl,
        productName,
        specsServiceUrl,
        part: "body",
        side: "back",
        role: "highlight",
      })
    : null;
  setStep(stepIndex, { status: "done" });

  return {
    generatedLayers,
    globalSides: {
      front: {
        imageUrl: uploadedFrontGlobalImageUrl,
        highlightUrl: uploadedFrontGlobalHighlightUrl,
      },
      ...(uploadedBackGlobalImageUrl && uploadedBackGlobalHighlightUrl
        ? {
            back: {
              imageUrl: uploadedBackGlobalImageUrl,
              highlightUrl: uploadedBackGlobalHighlightUrl,
            },
          }
        : {}),
    },
  };
}

export async function createImportProductRevision({
  productName,
  displayName,
  versionName,
  idPrextra,
  specsServiceUrl,
  preparedAssets,
  createdBy,
  steps,
  onStepsChange,
}: ImportCreateRevisionInput): Promise<ImportRunResult> {
  const stepIndex = getCreateRevisionStepIndex(steps);
  let currentSteps = updateStep(steps, stepIndex, {
    status: "running",
    detail: undefined,
  });
  onStepsChange(currentSteps);

  const snapshot = buildImportProductSnapshot({
    productName,
    displayName,
    versionName,
    idPrextra,
    generatedLayers: preparedAssets.generatedLayers,
    globalSides: preparedAssets.globalSides,
  });

  const response = await SpecsRevisionService.saveSpecRevision(
    {
      specType: "product",
      specName: snapshot.product.name,
      snapshot: snapshot.product,
      sourceRevisionId: null,
      createdBy,
      changeNote: "AI-assisted product import first pass",
    },
    { specsServiceUrl }
  );
  currentSteps = updateStep(currentSteps, stepIndex, { status: "done" });
  onStepsChange(currentSteps);

  return {
    productName: snapshot.product.name,
    versionName,
    revisionId: response.data?.revisionId as string,
  };
}

export type { ProgressStep };
